/**
 * ═══════════════════════════════════════════════════════════════════════
 *  Carrusel de Conferencistas — #conferencistas (/encuentro-2026)
 * ═══════════════════════════════════════════════════════════════════════
 *
 *  Sincroniza la tira de retratos y el escenario (retrato grande visible
 *  solo en tablet/móvil). La información de cada conferencista (overlay
 *  con especialidad/nombre/conferencia/descripción) vive DENTRO de su
 *  propia foto — no hay panel de texto separado. En desktop, mostrar u
 *  ocultar el overlay del activo es puro CSS (mismo .is-activo que ya
 *  anima flex-grow y grayscale); este script solo ajusta su aria-hidden
 *  y, en tablet/móvil, copia ese mismo texto hacia el escenario.
 *
 *  Arquitectura adaptada de src/js/equipo-medico.js del proyecto Advance
 *  Wellness (analizado como referencia técnica): mismo patrón de estado
 *  centralizado y eventos, sin GSAP, sin dependencias externas, con
 *  nombres y comportamiento propios de esta landing.
 *
 *  Accesibilidad
 *  - Navegación con flechas ← → cuando el foco está dentro del carrusel
 *  - aria-current en el retrato y el punto activos
 *  - El overlay del slide inactivo queda aria-hidden="true" (además de
 *    invisible por CSS): un lector de pantalla solo encuentra la info
 *    del conferencista activo
 *  - Respeta prefers-reduced-motion
 *
 *  No hay reproducción automática: el usuario controla el ritmo.
 * ═══════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  var carrusel = document.querySelector('[data-speakers-carrusel]');
  if (!carrusel) return;

  var slides  = Array.prototype.slice.call(carrusel.querySelectorAll('.evento-speakers__slide'));
  var thumbs  = Array.prototype.slice.call(carrusel.querySelectorAll('[data-speaker-ir].evento-speakers__thumb'));
  var puntos  = Array.prototype.slice.call(carrusel.querySelectorAll('.evento-speakers__punto'));
  var btnPrev = carrusel.querySelector('[data-speaker-prev]');
  var btnNext = carrusel.querySelector('[data-speaker-next]');
  var tira    = carrusel.querySelector('.evento-speakers__tira');

  // Overlay propio de cada slide (uno por conferencista, dentro de su foto)
  var overlays = slides.map(function (slide) {
    return slide.querySelector('.evento-speakers__overlay');
  });

  var escenarioAvif = carrusel.querySelector('[data-speaker-escenario-avif]');
  var escenarioWebp = carrusel.querySelector('[data-speaker-escenario-webp]');
  var escenarioImg  = carrusel.querySelector('[data-speaker-escenario-img]');

  // Campos de texto del escenario móvil/tablet: se sincronizan leyendo
  // el overlay del thumb activo, mismo criterio que la imagen (una sola
  // fuente de verdad por conferencista, sin duplicar datos en el JS).
  var CAMPOS_ESCENARIO = ['especialidad', 'nombre', 'conferencia', 'descripcion'];
  var escenarioCampos = {};
  CAMPOS_ESCENARIO.forEach(function (campo) {
    escenarioCampos[campo] = carrusel.querySelector('[data-speaker-escenario-' + campo + ']');
  });

  var total = slides.length;
  if (!total) return;

  var actual = 0;

  var sinMovimiento = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  /**
   * Si un retrato no carga (asset todavía pendiente), lo oculta en vez
   * de dejar visible el ícono de imagen rota: el degradado de fondo
   * declarado en .evento-speakers__foto queda como placeholder.
   */
  function vigilarError(img) {
    if (!img) return;
    img.addEventListener('error', function () {
      img.classList.add('is-error');
    });
  }

  Array.prototype.slice
    .call(carrusel.querySelectorAll('.evento-speakers__foto-img'))
    .forEach(vigilarError);

  /**
   * Centra el retrato activo dentro de la tira cuando esta tiene scroll
   * horizontal (tablet y móvil). En desktop la tira no tiene overflow y
   * esta función no produce ningún movimiento.
   */
  function centrarRetrato(indice) {
    if (!tira || !slides[indice]) return;

    var slide   = slides[indice];
    var destino = slide.offsetLeft - (tira.clientWidth / 2) + (slide.clientWidth / 2);

    if (typeof tira.scrollTo === 'function') {
      tira.scrollTo({
        left: Math.max(0, destino),
        behavior: sinMovimiento ? 'auto' : 'smooth'
      });
    } else {
      tira.scrollLeft = Math.max(0, destino);
    }
  }

  /**
   * Refleja el retrato Y la información (especialidad/nombre/
   * conferencia/descripción) del slide activo en el escenario grande
   * (visible solo en tablet/móvil). No duplica datos: los toma del
   * propio <picture> y <span class="evento-speakers__overlay"> del
   * thumb correspondiente, así que solo hay una fuente de verdad por
   * conferencista.
   */
  function sincronizarEscenario(indice) {
    var thumb = thumbs[indice];
    if (!thumb) return;

    if (escenarioImg) {
      var fuenteAvif = thumb.querySelector('source[type="image/avif"]');
      var fuenteWebp = thumb.querySelector('source[type="image/webp"]');
      var imgThumb   = thumb.querySelector('.evento-speakers__foto-img');

      if (escenarioAvif && fuenteAvif) escenarioAvif.srcset = fuenteAvif.srcset;
      if (escenarioWebp && fuenteWebp) escenarioWebp.srcset = fuenteWebp.srcset;
      if (imgThumb) escenarioImg.src = imgThumb.src;

      escenarioImg.classList.remove('is-error');
    }

    CAMPOS_ESCENARIO.forEach(function (campo) {
      var destino = escenarioCampos[campo];
      if (!destino) return;

      var origen = thumb.querySelector('.evento-speakers__' + campo);
      if (origen) destino.textContent = origen.textContent.trim();
    });
  }

  function ir(indice) {
    if (indice < 0) indice = total - 1;
    if (indice >= total) indice = 0;
    if (indice === actual) return;

    actual = indice;

    slides.forEach(function (slide, i) {
      slide.classList.toggle('is-activo', i === indice);
    });

    // El overlay del activo ya se muestra por CSS (.is-activo); aquí
    // solo sincronizamos su aria-hidden para que un lector de pantalla
    // encuentre únicamente la información del conferencista visible.
    overlays.forEach(function (overlay, i) {
      if (!overlay) return;
      overlay.setAttribute('aria-hidden', i === indice ? 'false' : 'true');
    });

    thumbs.forEach(function (thumb, i) {
      if (i === indice) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    puntos.forEach(function (punto, i) {
      punto.classList.toggle('is-activo', i === indice);
      if (i === indice) {
        punto.setAttribute('aria-current', 'true');
      } else {
        punto.removeAttribute('aria-current');
      }
    });

    sincronizarEscenario(indice);
    centrarRetrato(indice);
  }

  // ── Eventos ────────────────────────────────────────────────────────

  carrusel.addEventListener('click', function (evento) {
    var disparador = evento.target.closest('[data-speaker-ir]');
    if (!disparador || !carrusel.contains(disparador)) return;

    var indice = parseInt(disparador.getAttribute('data-speaker-ir'), 10);
    if (!isNaN(indice)) ir(indice);
  });

  if (btnPrev) {
    btnPrev.addEventListener('click', function () {
      ir(actual - 1);
    });
  }

  if (btnNext) {
    btnNext.addEventListener('click', function () {
      ir(actual + 1);
    });
  }

  carrusel.addEventListener('keydown', function (evento) {
    if (evento.key === 'ArrowLeft') {
      evento.preventDefault();
      ir(actual - 1);
    } else if (evento.key === 'ArrowRight') {
      evento.preventDefault();
      ir(actual + 1);
    }
  });

  // Deslizamiento táctil sobre la tira
  var inicioX = null;

  carrusel.addEventListener('touchstart', function (evento) {
    inicioX = evento.changedTouches[0].clientX;
  }, { passive: true });

  carrusel.addEventListener('touchend', function (evento) {
    if (inicioX === null) return;

    var desplazamiento = evento.changedTouches[0].clientX - inicioX;
    inicioX = null;

    if (Math.abs(desplazamiento) < 50) return;
    ir(desplazamiento < 0 ? actual + 1 : actual - 1);
  });

})();
